import React, { useEffect, useRef, useState } from "react";
import { ViewerSettings } from "../../types/settings";

type LanguageCode = ViewerSettings["language"];

interface LoadingScreenProps {
  progress?: number; // 0-100
  language?: LanguageCode;
  variant?: "minimal" | "gradient" | "carousel" | "progress";
  logoSrc?: string;
  companyName?: string;
  fontFamily?: string;
  background?: string;
  textColor?: string;
  mutedTextColor?: string;
  panelBackground?: string;
  panelBorderColor?: string;
  panelBoxShadow?: string;
  accentColor?: string;
}

/**
 * Public container for LoadingScreen that handles smooth mount/unmount
 * transitions. Consumers pass `visible`; the component animates an
 * entrance on mount and waits for the exit animation to complete before
 * unmounting itself.
 *
 * The entrance and exit timings are intentionally tuned to feel
 * cinematic without delaying perceived readiness:
 *   - enter: 600ms ease-out-expo (opacity 0→1, scale 0.98→1)
 *   - exit:  400ms ease-in-out  (opacity 1→0, scale 1→1.02)
 */
export interface LoadingScreenContainerProps extends LoadingScreenProps {
  /** When false, plays the exit animation then unmounts the screen. */
  visible: boolean;
  /** Optional override for the exit animation duration (ms). */
  exitDurationMs?: number;
}

const DEFAULT_EXIT_DURATION_MS = 400;

export function LoadingScreenContainer({
  visible,
  exitDurationMs = DEFAULT_EXIT_DURATION_MS,
  ...props
}: LoadingScreenContainerProps) {
  // Always render at first if visible was true on mount; otherwise wait.
  const [shouldRender, setShouldRender] = useState<boolean>(visible);
  const [phase, setPhase] = useState<"entering" | "visible" | "exiting">(
    visible ? "entering" : "visible",
  );
  const exitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const enterFrameRef = useRef<number | null>(null);

  useEffect(() => {
    if (visible) {
      // Cancel any pending exit unmount.
      if (exitTimeoutRef.current) {
        clearTimeout(exitTimeoutRef.current);
        exitTimeoutRef.current = null;
      }
      setShouldRender(true);
      setPhase("entering");
      // Move to "visible" on the next frame so the entrance class applies.
      enterFrameRef.current = requestAnimationFrame(() => {
        setPhase("visible");
      });
      return () => {
        if (enterFrameRef.current !== null) {
          cancelAnimationFrame(enterFrameRef.current);
          enterFrameRef.current = null;
        }
      };
    }

    // visible flipped to false. If we were never rendered, do nothing.
    if (!shouldRender) return;

    setPhase("exiting");
    exitTimeoutRef.current = setTimeout(() => {
      setShouldRender(false);
      exitTimeoutRef.current = null;
    }, exitDurationMs);
    return () => {
      if (exitTimeoutRef.current) {
        clearTimeout(exitTimeoutRef.current);
        exitTimeoutRef.current = null;
      }
    };
  }, [visible, shouldRender, exitDurationMs]);

  if (!shouldRender) return null;

  return (
    <div
      data-loading-phase={phase}
      style={{
        // Cover the viewport regardless of any positioned ancestor's
        // stacking context. Using `fixed` here avoids subtle bugs where a
        // transformed ancestor or sibling positioned absolute with an
        // overlapping z-index would paint chrome over the loading screen.
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100vh",
        // Higher than any chrome (StepControls=50, panels=100, header=110,
        // tutorial overlay=200). Below dialogs (200-250) so DialogContent
        // can still appear above a loading state if it ever needs to (e.g.,
        // an error dialog). Adjust upward if a future component needs to
        // appear above the loading screen.
        zIndex: 1100,
        opacity: phase === "entering" ? 0 : phase === "exiting" ? 0 : 1,
        transform:
          phase === "entering"
            ? "scale(0.98)"
            : phase === "exiting"
              ? "scale(1.02)"
              : "scale(1)",
        transition:
          phase === "exiting"
            ? `opacity ${exitDurationMs}ms ease-in-out, transform ${exitDurationMs}ms ease-in-out`
            : "opacity 600ms cubic-bezier(0.16, 1, 0.3, 1), transform 600ms cubic-bezier(0.16, 1, 0.3, 1)",
        willChange: "opacity, transform",
        pointerEvents: phase === "exiting" ? "none" : "auto",
      }}
      data-oid="loading-screen-container"
    >
      <LoadingScreen {...props} />
    </div>
  );
}

function getLoadingCopy(language: LanguageCode) {
  switch (language) {
    case "es":
      return {
        companyLogo: "Logotipo de la empresa",
        loadingModel: "Cargando modelo 3D",
        loadingModelShort: "Cargando modelo",
        preparingVisualization: "Preparando visualizaci\u00f3n 3D",
        phrases: [
          "Cargando modelo 3D",
          "Inicializando escena",
          "Procesando geometr\u00eda",
          "Listo para renderizar",
        ],

        preparing: "Preparando visualizaci\u00f3n",
      };
    case "fr":
      return {
        companyLogo: "Logo de l\u2019entreprise",
        loadingModel: "Chargement du mod\u00e8le 3D",
        loadingModelShort: "Chargement du mod\u00e8le",
        preparingVisualization: "Pr\u00e9paration de la visualisation 3D",
        phrases: [
          "Chargement du mod\u00e8le 3D",
          "Initialisation de la sc\u00e8ne",
          "Traitement de la g\u00e9om\u00e9trie",
          "Pr\u00eat \u00e0 afficher",
        ],

        preparing: "Pr\u00e9paration de la visualisation",
      };
    case "de":
      return {
        companyLogo: "Firmenlogo",
        loadingModel: "3D-Modell wird geladen",
        loadingModelShort: "Modell wird geladen",
        preparingVisualization: "3D-Visualisierung wird vorbereitet",
        phrases: [
          "3D-Modell wird geladen",
          "Szene wird initialisiert",
          "Geometrie wird verarbeitet",
          "Bereit zum Rendern",
        ],

        preparing: "Visualisierung wird vorbereitet",
      };
    case "zh":
      return {
        companyLogo: "\u516c\u53f8\u6807\u5fd7",
        loadingModel: "\u6b63\u5728\u52a0\u8f7d 3D \u6a21\u578b",
        loadingModelShort: "\u6b63\u5728\u52a0\u8f7d\u6a21\u578b",
        preparingVisualization:
          "\u6b63\u5728\u51c6\u5907 3D \u53ef\u89c6\u5316",
        phrases: [
          "\u6b63\u5728\u52a0\u8f7d 3D \u6a21\u578b",
          "\u6b63\u5728\u521d\u59cb\u5316\u573a\u666f",
          "\u6b63\u5728\u5904\u7406\u51e0\u4f55\u6570\u636e",
          "\u51c6\u5907\u6e32\u67d3",
        ],

        preparing: "\u6b63\u5728\u51c6\u5907\u53ef\u89c6\u5316",
      };
    case "en":
    default:
      return {
        companyLogo: "Company Logo",
        loadingModel: "Loading 3D Model",
        loadingModelShort: "Loading Model",
        preparingVisualization: "Preparing 3D visualization",
        phrases: [
          "Loading 3D Model",
          "Initializing Scene",
          "Processing Geometry",
          "Ready to Render",
        ],

        preparing: "Preparing visualization",
      };
  }
}

export function LoadingScreen({
  progress = 0,
  language = "en",
  variant = "gradient",
  logoSrc = "/weijia-construction-logo.png",
  companyName,
  fontFamily = "Arial, sans-serif",
  background = "linear-gradient(135deg, #3a3a3a 0%, #2a2a2a 100%)",
  textColor = "#e0e0e0",
  mutedTextColor = "rgba(255, 255, 255, 0.7)",
  panelBackground = "rgba(255, 255, 255, 0.08)",
  panelBorderColor = "rgba(255, 255, 255, 0.2)",
  panelBoxShadow = "0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 1px rgba(255, 255, 255, 0.2)",
  accentColor = "#667eea",
}: LoadingScreenProps) {
  const copy = getLoadingCopy(language);
  const [dots, setDots] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setDots((prev) => (prev + 1) % 4);
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // Minimal variant: clean and simple with animated spinner
  if (variant === "minimal") {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          margin: 0,
          padding: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background,
          fontFamily,
          perspective: "1000px",
        }}
        data-oid="6pj4q0l"
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "clamp(120px, 35vw, 400px)",
            width: "clamp(120px, 35vw, 400px)",
            marginBottom: "3rem",
            background: panelBackground,
            backdropFilter: "blur(20px)",
            borderRadius: "24px",
            border: `1px solid ${panelBorderColor}`,
            boxShadow: panelBoxShadow,
            // Initial transform matches the start frame of float3d for a
            // seamless transition from entrance into the idle float.
            transform: "perspective(1200px) rotateX(0deg) rotateY(0deg)",
            // Stagger: panel enters slightly after the backdrop fades in,
            // then the slow idle float takes over.
            animation:
              "loadingPanelEnter 700ms cubic-bezier(0.16, 1, 0.3, 1) 120ms both, " +
              "loadingPanelFloat 6s ease-in-out 820ms infinite, " +
              "loadingPanelPulse 2.4s ease-in-out 820ms infinite",
          }}
          data-oid="ko8uoaz"
        >
          <img
            src={logoSrc}
            alt={copy.companyLogo}
            style={{
              height: "80%",
              width: "auto",
              objectFit: "contain",
            }}
            data-oid="jnp_l-t"
          />
        </div>
        {companyName ? (
          <div
            style={{
              fontSize: "clamp(1rem, 2vw, 1.35rem)",
              color: mutedTextColor,
              fontWeight: 600,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              marginBottom: "0.75rem",
              animation:
                "loadingTextEnter 600ms cubic-bezier(0.16, 1, 0.3, 1) 240ms both",
            }}
            data-oid=".se_:o1"
          >
            {companyName}
          </div>
        ) : null}
        <div
          style={{
            fontSize: "16px",
            color: textColor,
            fontWeight: 500,
            animation:
              "loadingTextEnter 600ms cubic-bezier(0.16, 1, 0.3, 1) 320ms both",
          }}
          data-oid="-qdm2:j"
        >
          {copy.loadingModel}
          {".".repeat(dots)}
        </div>
        <style data-oid="17f6jvw">{`
          @keyframes loadingPanelEnter {
            from {
              opacity: 0;
              transform: perspective(1200px) translateY(24px) scale(0.92) rotateX(0deg) rotateY(0deg);
            }
            to {
              opacity: 1;
              transform: perspective(1200px) translateY(0) scale(1) rotateX(0deg) rotateY(0deg);
            }
          }
          @keyframes loadingPanelFloat {
            0%, 100% {
              transform: perspective(1200px) translateY(0) rotateX(2deg) rotateY(-2deg);
            }
            50% {
              transform: perspective(1200px) translateY(-8px) rotateX(-2deg) rotateY(2deg);
            }
          }
          @keyframes loadingPanelPulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.92; }
          }
          @keyframes loadingTextEnter {
            from { opacity: 0; transform: translateY(8px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </div>
    );
  }

  // Gradient variant: modern gradient background with rotating logo
  if (variant === "gradient") {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          margin: 0,
          padding: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background,
          fontFamily,
          position: "relative",
        }}
        data-oid="p4rcylr"
      >
        {/* Animated background orbs */}
        <div
          style={{
            position: "absolute",
            top: "20%",
            left: "10%",
            width: "300px",
            height: "300px",
            background: `${accentColor}22`,
            borderRadius: "50%",
            filter: "blur(60px)",
            animation: "float 8s ease-in-out infinite",
          }}
          data-oid="yfwsy7a"
        />

        <div
          style={{
            position: "absolute",
            bottom: "10%",
            right: "15%",
            width: "250px",
            height: "250px",
            background: `${accentColor}18`,
            borderRadius: "50%",
            filter: "blur(50px)",
            animation: "float 10s ease-in-out infinite reverse",
          }}
          data-oid="l_.smaq"
        />

        {/* Content */}
        <div
          style={{ position: "relative", zIndex: 10, textAlign: "center" }}
          data-oid="gre88rd"
        >
          <img
            src={logoSrc}
            alt={copy.companyLogo}
            style={{
              height: "100px",
              width: "auto",
              marginBottom: "2rem",
              filter: "drop-shadow(0 10px 25px rgba(0, 0, 0, 0.2))",
              animation: "spin 3s linear infinite",
            }}
            data-oid="yos1lb5"
          />

          <div
            style={{
              fontSize: "22px",
              color: textColor,
              fontWeight: 600,
              letterSpacing: "1px",
            }}
            data-oid="t03gf.n"
          >
            {copy.loadingModelShort}
            {".".repeat(dots)}
          </div>
          <div
            style={{
              fontSize: "13px",
              color: mutedTextColor,
              marginTop: "1rem",
            }}
            data-oid="2b7rpiz"
          >
            {copy.preparingVisualization}
          </div>
        </div>

        <style data-oid="mantm..">{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(30px); }
          }
        `}</style>
      </div>
    );
  }

  // Progress variant: with progress bar
  if (variant === "progress") {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          margin: 0,
          padding: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background,
          fontFamily,
        }}
        data-oid="-x63glc"
      >
        <img
          src={logoSrc}
          alt={copy.companyLogo}
          style={{
            height: "90px",
            width: "auto",
            marginBottom: "3rem",
            filter: "drop-shadow(0 4px 12px rgba(0, 0, 0, 0.08))",
          }}
          data-oid="v7zn3a8"
        />

        <div
          style={{
            fontSize: "18px",
            color: textColor,
            fontWeight: 600,
            marginBottom: "2rem",
          }}
          data-oid="p7cnqbk"
        >
          {copy.loadingModel}
        </div>

        {/* Progress bar */}
        <div
          style={{
            width: "280px",
            height: "6px",
            backgroundColor: `${accentColor}22`,
            borderRadius: "3px",
            overflow: "hidden",
            marginBottom: "1.5rem",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          }}
          data-oid="r0-g4o-"
        >
          <div
            style={{
              height: "100%",
              width: `${Math.min(progress, 100)}%`,
              background: `linear-gradient(90deg, ${accentColor}, ${textColor})`,
              borderRadius: "3px",
              transition: "width 0.3s ease-out",
            }}
            data-oid="auq0_cn"
          />
        </div>

        <div
          style={{ fontSize: "12px", color: mutedTextColor }}
          data-oid="3:xbgm7"
        >
          {Math.round(progress)}%
        </div>
      </div>
    );
  }

  // Carousel variant: logo with rotating text
  if (variant === "carousel") {
    const phrases = copy.phrases;
    const activePhrase = phrases[Math.floor((dots / 3) % phrases.length)];

    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          margin: 0,
          padding: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background,
          fontFamily,
          position: "relative",
        }}
        data-oid="0ei1z70"
      >
        {/* Grid background */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "linear-gradient(rgba(255,255,255,.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.05) 1px, transparent 1px)",
            backgroundSize: "50px 50px",
            opacity: 0.5,
          }}
          data-oid="zw3tcj6"
        />

        {/* Content */}
        <div
          style={{ position: "relative", zIndex: 10, textAlign: "center" }}
          data-oid="fotd9ik"
        >
          <div
            style={{
              width: "120px",
              height: "120px",
              margin: "0 auto 2rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              background: `linear-gradient(135deg, ${accentColor}, ${textColor})`,
              padding: "8px",
              animation: "rotateBorder 4s linear infinite",
            }}
            data-oid="hxr0obd"
          >
            <img
              src={logoSrc}
              alt={copy.companyLogo}
              style={{
                height: "100%",
                width: "auto",
                objectFit: "contain",
              }}
              data-oid="34ok.ya"
            />
          </div>

          <div
            style={{
              fontSize: "20px",
              color: textColor,
              fontWeight: 600,
              letterSpacing: "0.5px",
              minHeight: "28px",
            }}
            data-oid="-6ados3"
          >
            {activePhrase}
          </div>
          <div
            style={{
              fontSize: "12px",
              color: mutedTextColor,
              marginTop: "0.5rem",
            }}
            data-oid="j677nyu"
          >
            {copy.preparing}
          </div>
        </div>

        <style data-oid="qx62yl.">{`
          @keyframes rotateBorder {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Default fallback
  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        margin: 0,
        padding: 0,
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background,
      }}
      data-oid="c5k4rdv"
    >
      <div
        style={{ fontSize: "18px", color: textColor, fontFamily }}
        data-oid="jj_08d0"
      >
        {copy.loadingModel}...
      </div>
    </div>
  );
}
